import React, { useState, useRef, useEffect } from 'react';
import {
  Upload, FileText, Loader2, Sparkles, ClipboardType, CheckCircle2, AlertTriangle, Info, Plus, Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { SOURCE_LABEL, UPLOAD_SOURCE_OPTIONS } from '@/lib/format';
import { toast } from 'sonner';
import { track } from '@/lib/analytics';

const ACCEPT = '.pdf,.docx,.eml,.msg,.txt';

/**
 * "Add Anything" — a global capture entry point for busy PMs.
 * Pick a project, paste text or drop a file, and Recoup finds any opportunity.
 */
export const QuickCaptureDialog = ({ onDone, trigger }) => {
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState('');
  const [tab, setTab] = useState('paste');
  const [sourceType, setSourceType] = useState('email');
  const [subject, setSubject] = useState('');
  const [sender, setSender] = useState('');
  const [content, setContent] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (open) {
      api.get('/projects').then((r) => {
        setProjects(r.data);
        if (r.data.length && !projectId) setProjectId(r.data[0].id);
      }).catch(() => {});
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const reset = () => { setSubject(''); setSender(''); setContent(''); setFile(null); setResult(null); };

  const finish = (analysis) => {
    setResult(analysis);
    track('event_uploaded', { via: 'quick_capture', source_type: sourceType, status: analysis?.status });
    if (analysis?.status === 'scope_change') toast.success(`Opportunity found: ${analysis.title || ''}`);
    else if (analysis?.status === 'no_change') toast('Reviewed — nothing to claim here.');
    else if (analysis?.status === 'failed') toast.error('Review failed. The item was still saved.');
    else if (analysis?.status === 'skipped') toast('Saved. No readable text to review.');
    onDone && onDone(analysis);
  };

  const submitPaste = async () => {
    if (!projectId) { toast.error('Pick a project first'); return; }
    if (!content.trim()) { toast.error('Paste the communication text first'); return; }
    setLoading(true); setResult(null);
    try {
      const r = await api.post('/detections/analyze', { project_id: projectId, source_type: sourceType, subject, sender, content });
      finish(r.data.analysis);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Review failed'); }
    finally { setLoading(false); }
  };

  const submitFile = async () => {
    if (!projectId) { toast.error('Pick a project first'); return; }
    if (!file) { toast.error('Choose a file'); return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('project_id', projectId);
      fd.append('source_type', sourceType);
      if (subject) fd.append('subject', subject);
      if (sender) fd.append('sender', sender);
      const r = await api.post('/events/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (r.data.extraction?.status !== 'ok') {
        toast.error(r.data.extraction?.error || 'Could not read text from that file');
        setResult({ status: 'skipped', extraction: r.data.extraction });
      } else {
        finish({ ...r.data.analysis, extraction: r.data.extraction });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button className="bg-money hover:bg-money-700 shadow-soft" data-testid="add-anything-btn">
            <Zap className="h-4 w-4 mr-2" />Add Anything
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="quick-capture-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Zap className="h-4 w-4 text-money-600" />Add anything</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500 -mt-1">Paste an email, RFI or note — or drop a file. Recoup reads it and tells you if there's an opportunity.</p>

        {projects.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 p-4 text-center text-sm text-slate-500" data-testid="capture-no-projects">
            Create a project first so Recoup knows what to compare against.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Project</Label>
                <Select value={projectId} onValueChange={setProjectId}>
                  <SelectTrigger className="mt-1" data-testid="capture-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                  <SelectContent>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Type</Label>
                <Select value={sourceType} onValueChange={setSourceType}>
                  <SelectTrigger className="mt-1" data-testid="capture-source-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {UPLOAD_SOURCE_OPTIONS.map((k) => <SelectItem key={k} value={k}>{SOURCE_LABEL[k] || k}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Subject (optional)</Label>
              <Input className="mt-1" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. RFI 88 - extra ductwork" data-testid="capture-subject" />
            </div>

            <Tabs value={tab} onValueChange={setTab} className="mt-1">
              <TabsList className="w-full">
                <TabsTrigger value="paste" className="flex-1" data-testid="capture-tab-paste"><ClipboardType className="h-4 w-4 mr-1.5" />Paste text</TabsTrigger>
                <TabsTrigger value="file" className="flex-1" data-testid="capture-tab-file"><FileText className="h-4 w-4 mr-1.5" />Upload file</TabsTrigger>
              </TabsList>
              <TabsContent value="paste" className="mt-3">
                <Textarea rows={6} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Paste the email, RFI, site instruction or meeting note..." data-testid="capture-content" />
                <Button onClick={submitPaste} disabled={loading} className="w-full mt-3 bg-money hover:bg-money-700" data-testid="capture-analyze-paste">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Review this
                </Button>
              </TabsContent>
              <TabsContent value="file" className="mt-3">
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="capture-file-input"
                  onChange={(e) => setFile(e.target.files?.[0] || null)} />
                <button type="button" onClick={() => fileRef.current?.click()}
                  className="w-full rounded-xl border-2 border-dashed border-slate-200 hover:border-money/40 bg-slate-50 px-4 py-6 text-center transition-colors">
                  <Upload className="h-5 w-5 mx-auto text-slate-400" />
                  <div className="text-sm text-slate-600 mt-2">{file ? file.name : 'Click to choose a file'}</div>
                  <div className="text-[11px] text-slate-400 mt-1">PDF, DOCX, EML, MSG, TXT</div>
                </button>
                <Button onClick={submitFile} disabled={loading || !file} className="w-full mt-3 bg-money hover:bg-money-700" data-testid="capture-analyze-file">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />} Read &amp; review
                </Button>
              </TabsContent>
            </Tabs>

            {result && (
              <div className="mt-1 rounded-lg border p-3 text-sm" data-testid="capture-result">
                {result.status === 'scope_change' && (
                  <div className="flex items-start gap-2 text-money-700"><CheckCircle2 className="h-4 w-4 mt-0.5" />
                    <div><span className="font-semibold">Opportunity found.</span> {result.title}</div></div>
                )}
                {result.status === 'no_change' && (
                  <div className="flex items-start gap-2 text-slate-600"><Info className="h-4 w-4 mt-0.5" />
                    <div><span className="font-semibold">Nothing to claim here.</span> {result.summary}</div></div>
                )}
                {(result.status === 'skipped' || result.status === 'failed') && (
                  <div className="flex items-start gap-2 text-warn-700"><AlertTriangle className="h-4 w-4 mt-0.5" />
                    <div>{result.extraction?.error || 'Saved. Not reviewed.'}</div></div>
                )}
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
